import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { FILE_SCOPE_POST } from '../../../../constants/file'
import { SIZE } from '../../../../constants/size'
import { useInitData } from '../../../../hooks'
import { hasTinyMceHtmlVisibleText } from '../../../../utils/html'
import { AITextGenerationType } from '../../../AITextGeneration'
import {
  AITextGenerationField,
  applyAITextToTextareaField,
  applyAITextToWysiwygField,
  useAITextGenerationDraftGuard,
} from '../../../AITextGenerationField'
import { AttachActionSelect } from '../../../AttachActionSelect'
import { Field } from '../../../Field'
import {
  FileList,
  FilePreview,
  useAttachStorageFiles,
  useFileUpload,
} from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import { Content } from '../../../Modal'
import { MoreActionsButton } from '../../../MoreActionsButton'
import { isPostTypeTicker } from '../../../Post'
import { useWysiwygEditorFullScreenHeight } from '../../../WysiwygEditor'
import { NewsboardMessageField } from '../../NewsboardMessageField'
import {
  NEWSBOARD_CREATION_FIELD_NAME_TEXT_TEMPLATE,
  NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE,
  NEWSBOARD_POST_FORM_FIELD_NAME_TITLE,
  NEWSBOARD_POST_FORM_FIELD_NAME_TYPE,
  NEWSBOARD_POST_FORM_FIELD_REQUESTS,
  NEWSBOARD_POST_FORM_NOTIFICATION_AI_DRAFT_NOT_APPLIED,
  NEWSBOARD_POST_FORM_TICKER_MESSAGE_FIELD_MAX_LENGTH,
} from '../NewsboardPostForm.constants'
import {
  useAttachServiceRequests,
  useDateValidation,
  useNewsboardPostFormAttach,
  useSelectTextTemplate,
} from '../NewsboardPostForm.hooks'
import { checkNewsboardPostFormForEmptyRequired } from '../NewsboardPostForm.utils'

import { AttachedServiceRequests } from './AttachedServiceRequests'
import { CommentsField } from './CommentsField'
import { useMoreActions } from './FormFields.hooks'
import { PostAgeField } from './PostAgeField'
import { PostTypeField } from './PostTypeField'
import { RecipientsField } from './RecipientsField'
import { TimeRangeField } from './TimeRangeField'
import { TitleField } from './TitleField'

import * as styles from './FormFields.module.css'

function FormFields({
  ContentComponent = Content,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  files,
  isAdminUserAcc,
  isStaffUserAcc,
  notifications,
  recipientInitialValues,
  resetInitialValues,
  setFiles,
  setModal,
  setStorageFiles,
  storageFiles,
  onCancel,
  onCloseModal,
  onRemoveNotification,
  onSetNotification,
}) {
  const { dirty, isSubmitting, values } = useFormikContext()

  const { t } = useTranslation()

  const { ai_features: aiFeatures } = useInitData()

  const { contentRef, contentWrapperRef, editorRef, handleEditorInit } =
    useWysiwygEditorFullScreenHeight({
      isFullScreen: contentComponentProps?.isFullScreen,
    })

  const { ensureDraftApplied, handleAIModeChange, isAIError } =
    useAITextGenerationDraftGuard({
      notificationId: NEWSBOARD_POST_FORM_NOTIFICATION_AI_DRAFT_NOT_APPLIED,
      notifications,
      onRemoveNotification,
      onSetNotification,
    })

  const typeValue = values[NEWSBOARD_POST_FORM_FIELD_NAME_TYPE]

  const isTickerType = isPostTypeTicker(typeValue.value)

  function handleApplyAITextToTextarea(generatedText, currentValue, action) {
    return applyAITextToTextareaField({
      action,
      currentValue,
      generatedText,
      maxLength: NEWSBOARD_POST_FORM_TICKER_MESSAGE_FIELD_MAX_LENGTH,
    })
  }

  function handleApplyAITextToWysiwyg(generatedText, currentValue, action) {
    return applyAITextToWysiwygField({
      action,
      currentValue,
      editorRef,
      generatedText,
    })
  }

  const preparedFiles = [...storageFiles, ...files]

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files: preparedFiles })

  const { handleAddStorageFile, handleRemoveStorageFile } =
    useAttachStorageFiles({
      onSetFiles: setStorageFiles,
    })

  const {
    filesError,
    filesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  useFormValidationErrorsNotification({
    checkFormikFormEmptyRequired: checkNewsboardPostFormForEmptyRequired,
    files: { filesError, filesUploading },
    onRemoveNotification,
    onSetNotification,
  })

  function handleDeleteAttachment(id) {
    if (storageFiles.find(i => i.id === id)) {
      handleRemoveStorageFile(id)
    } else {
      handleRemoveFile(id)
    }
  }

  const { dateEndBeforeDateStart, handleValidateDate } = useDateValidation({
    removeNotification: onRemoveNotification,
    setNotification: onSetNotification,
  })

  useSelectTextTemplate({
    addFiles: handleUploadFile,
    deleteFile: handleExcludeFile,
    editorRef,
    files,
    setModal,
    onCloseModal: onCloseModal,
  })

  const { handleOpenAttachServiceRequest, handleRemoveAttachedRequest } =
    useAttachServiceRequests({
      setModal,
      onCloseModal,
    })

  const { attachActionOptions, handleAttachAction } =
    useNewsboardPostFormAttach({
      selectedStorageFiles: storageFiles,
      onAttachFilesFromStorage: handleAddStorageFile,
      onCloseModal,
      onSetModal: setModal,
    })

  const { moreActions } = useMoreActions({
    onOpenAttachServiceRequest: handleOpenAttachServiceRequest,
  })

  function handleCheckOnSubmit(event) {
    if (!ensureDraftApplied(event)) {
      return
    }

    if (dateEndBeforeDateStart) {
      handleValidateDate()
    }
  }

  function handleCancel() {
    return onCancel({ dirty, files, values })
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        disabled={isSubmitting}
        file={file}
        key={key}
        onClick={handleClickFile}
        onRemoveFile={handleDeleteAttachment}
      />
    )
  }

  const showFiles =
    files?.length > 0 ||
    filesUploading?.length > 0 ||
    storageFiles?.length > 0 ||
    filesError?.length > 0

  const messageField = (
    <NewsboardMessageField
      skipFieldWrapper
      isStaffUserAcc={isStaffUserAcc}
      name={NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE}
      templateFieldName={NEWSBOARD_CREATION_FIELD_NAME_TEXT_TEMPLATE}
      ticker={isTickerType}
      onInitEditor={handleEditorInit}
    />
  )

  return (
    <FormBody
      actionsProps={{
        ...(isStaffUserAcc
          ? {
              More: MoreActionsButton,
              moreProps: { actions: moreActions, text: t('Common.More') },
            }
          : null),
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: isTickerType
          ? null
          : isStaffUserAcc
            ? AttachActionSelect
            : undefined,
        cancelProps: {
          onClick: handleCancel,
        },
        className: styles.actions,
        isSubmitting,
        submitProps: {
          isLoading: isSubmitting,
          title: 'Modal.NewsboardPublishBtnKey',
          onClick: handleCheckOnSubmit,
        },
        uploadProps: {
          placeholder: 'Newsboard.AttachActionKey',
          onFinishUpload: handleUploadFile,
          onStartUpload: handleStartUploading,
          onUploadErrors: handleUploadErrors,
          ...(isStaffUserAcc
            ? { options: attachActionOptions, onClick: handleAttachAction }
            : null),
        },
      }}
    >
      <ContentComponent
        {...contentComponentProps}
        containerClassName={styles.contentContainer}
        contentRef={contentRef}
        contentWrapperRef={contentWrapperRef}
        size={contentSize}
      >
        <FormInlineNotifications notifications={notifications} />

        <FormFieldsContainer className={styles.fieldsContainer}>
          {isStaffUserAcc ? (
            <PostTypeField
              resetInitialValues={resetInitialValues}
              setFiles={setFiles}
              setModal={setModal}
              setStorageFiles={setStorageFiles}
              onCloseModal={onCloseModal}
            />
          ) : null}
          <TitleField ticker={isTickerType} />
          <Field
            required
            shouldAdaptToMobileScreen
            label={`${t('Newsboard.MessageLabelKey')}:`}
          >
            {aiFeatures?.text_generation ? (
              <AITextGenerationField
                fieldContent={values[NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE]}
                fieldName={NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE}
                hasExistingContent={hasTinyMceHtmlVisibleText(
                  values[NEWSBOARD_POST_FORM_FIELD_NAME_MESSAGE]
                )}
                isError={isAIError}
                key={typeValue.value}
                subjectFieldName={NEWSBOARD_POST_FORM_FIELD_NAME_TITLE}
                type={AITextGenerationType.Post}
                onApplyText={
                  isTickerType
                    ? handleApplyAITextToTextarea
                    : handleApplyAITextToWysiwyg
                }
                onModeChange={handleAIModeChange}
              >
                {messageField}
              </AITextGenerationField>
            ) : (
              messageField
            )}
          </Field>
          <RecipientsField
            isAdminUserAcc={isAdminUserAcc}
            isStaffUserAcc={isStaffUserAcc}
            recipientInitialValues={recipientInitialValues}
            setModal={setModal}
            ticker={isTickerType}
            onCloseModal={onCloseModal}
          />
          {isStaffUserAcc ? <TimeRangeField /> : null}
          <PostAgeField />
          {!isTickerType && isStaffUserAcc ? <CommentsField /> : null}
          {showFiles ? (
            <Field
              shouldAdaptToMobileScreen
              disabled={isSubmitting}
              label={`${t('Newsboard.AttachLabelKey')}:`}
            >
              <FileList
                errorItems={filesError}
                files={preparedFiles}
                renderItem={renderFile}
                uploadingItems={filesUploading}
                onRemoveFile={handleRemoveFile}
              />
            </Field>
          ) : null}
          {values[NEWSBOARD_POST_FORM_FIELD_REQUESTS].length > 0 ? (
            <AttachedServiceRequests
              disabled={isSubmitting}
              onRemoveAttachedRequest={handleRemoveAttachedRequest}
            />
          ) : null}
          <FilesPreviewGallery
            files={preparedFiles}
            initActive={activeFileIndex}
            scope={FILE_SCOPE_POST}
            onClose={handleCloseFilePreview}
          />
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default FormFields
